import React from 'react';
import { Box, Flex } from 'rebass';
import styled from 'styled-components';
import { Controller, Scene } from 'react-scrollmagic';

import ContentContainer from '../../components/ContentContainer/ContentContainer';
import Image from '../../components/Image/Image';
import SocialShare from '../../components/SocialShare/SocialShare';
import Typography, {
  TypographyContextProvider,
} from '../../components/Typography/Typography';

import { theme } from '../../styles/theme';
import colourMatching from '../../utils/colourMatching';
import createMemo from '../../utils/storyblokRelationsMemo';
import isFadeEnabled from '../../utils/isFadeEnabled';
import { NewsItemFootnoteSectionStoryblok } from '../../types/storyblok';

const authorsMemo = createMemo();

const Avatar = styled(Image)`
  border-radius: 9999px;
  height: 131px;
  margin-bottom: ${theme.space[5]};
  margin-right: ${theme.space[5]};
  overflow: hidden;
  width: 131px;

  @media (min-width: ${theme.breakpoint.lg}) {
    margin-bottom: ${theme.space[3]};
  }
`;

/**
 * Renders a <NewsItemFootnoteSection /> component
 * @param {Object} props
 * @param {string} props.authorLabel
 * @param {string} [props.authors]
 * @param {string} [props.colourScheme]
 * @param {string} props.shareLabel
 */
const NewsItemFootnoteSection = ({
  authorLabel,
  authors: authorsRel,
  colourScheme,
  shareLabel,
}: NewsItemFootnoteSectionStoryblok) => {
  const authors = authorsMemo(authorsRel);

  return (
    <TypographyContextProvider value={{ colourScheme }}>
      <Box
        as="section"
        pt={['55px', '120px']}
        pb={[8, 9]}
        backgroundColor={colourMatching(colourScheme)}
      >
        <Controller globalSceneOptions={{ triggerHook: 'onEnter' }}>
          <Scene classToggle="full-opacity">
            <Box className={isFadeEnabled() ? 'zero-opacity' : ''}>
              <ContentContainer
                as={Flex}
                alignItems={['flex-start', , 'flex-end']}
                justifyContent="space-between"
                flexDirection={['column', , 'row']}
              >
                {authors ? (
                  <Box mb={[5, , 0]}>
                    <Flex flexDirection="row">
                      {authors.map((author) => (
                        <Avatar
                          key={author.uuid}
                          blurLoad
                          minSize="100x100"
                          src={author.content.photo}
                        />
                      ))}
                    </Flex>
                    <Typography component="strong">{authorLabel}</Typography>
                    <Flex flexDirection="row">
                      {authors.map((author, i, arr) => (
                        <>
                          <Typography
                            key={author.uuid}
                            component="a"
                            href={`https://www.linkedin.com/in/${author.content.linkedinId}`}
                            target="_blank"
                          >
                            {author.content.fullName}
                          </Typography>
                          {i < arr.length - 1 ? (
                            <Typography inline>&nbsp;&amp;&nbsp;</Typography>
                          ) : null}
                        </>
                      ))}
                    </Flex>
                  </Box>
                ) : null}
                <Box>
                  <Typography component="strong">{shareLabel}</Typography>
                  <SocialShare colourScheme={colourScheme} size={35} />
                </Box>
              </ContentContainer>
            </Box>
          </Scene>
        </Controller>
      </Box>
    </TypographyContextProvider>
  );
};

export default NewsItemFootnoteSection;
